import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  styled,
  Button,
  IconButton,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { IoIosAddCircleOutline } from "react-icons/io";
import { HiOutlineTrash } from "react-icons/hi";
import { useDate, usePersonalInfo } from "../../hooks";
import { PERSONAL_INFORMATION_API, FILTER_API } from "../../api";
import {
  setPersonalInformationData,
  updatePersonalInformationData,
  addNewChild,
  editChild,
  removeChild,
  removeMaritalInfo,
  setReloadPersonalInformationData,
} from "../../slice";
import BaseSwitch from "../all-inputs/BaseSwitch";
import BaseCheckbox from "../all-inputs/BaseCheckbox";
import DatePicker from "../DatePicker";
import Select from "../Select";
import Input from "../Input";
import AddField from "./AddField";
import BaseTextArea from "../all-inputs/BaseTextArea";
import { BaseRadioGroup } from "../inputs/RadioGroup";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  borderRadius: 5,
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<IoIosAddCircleOutline fontSize={24} />} {...props} />
))(({ theme }) => ({
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: "10px",
  borderRadius: 5,
}));

function PersonalInfo({ hasEditAccess, disabled = false, pageErrors = {} }) {
  const dispatch = useDispatch();
  const { data, reloadPage } = useSelector((state) => state.PersonalInformation);
  const { userInfo } = useSelector((state) => state.UserCurrentState);
  const { simpleGregorianToPersian } = useDate();
  const { handleSubmit: handleSubmitForm } = usePersonalInfo({ hasEditAccess });
  const [expanded, setExpanded] = useState("panel2");
  const [isSingle, setIsSingle] = useState(false);

  const gender = [
    { name: "پسر", value: 1 },
    { name: "دختر", value: 2 },
  ];

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleMaritalStatusChange = (event) => {
    dispatch(
      updatePersonalInformationData({
        key: "marriageHistoryCode",
        value: event.target.value,
        required: true,
      })
    );
    if (parseInt(event.target.value) === 7001) {
      dispatch(removeMaritalInfo());
      setIsSingle(true);
    } else {
      setIsSingle(false);
    }
  };

  const { refetch: getPersonalInformation } = useQuery(
    ["getPersonalInformation"],
    () => PERSONAL_INFORMATION_API.GetPersonalInformation(),
    {
      onSuccess: ({ data: personalInformation }) => {
        let children = [];
        let separationDate = "";
        dispatch(setReloadPersonalInformationData(false));
        if (personalInformation.success && personalInformation.data) {
          if (personalInformation.data.separationDate) {
            separationDate = simpleGregorianToPersian(
              personalInformation.data.separationDate.split("T")[0]
            );
          }
          if (personalInformation.data.children) {
            children = personalInformation.data.children.map((child) => {
              if (child.birthDate) {
                return {
                  ...child,
                  birthDate: simpleGregorianToPersian(child.birthDate.split("T")[0]),
                };
              }
              return child;
            });
          }
          dispatch(
            setPersonalInformationData({
              ...personalInformation.data,
              separationDate,
              militaryServiceStatus:
                personalInformation.data.militaryServiceStatus === 0
                  ? 5
                  : personalInformation.data.militaryServiceStatus,
              children,
            })
          );
        }
      },
      staleTime: Infinity,
    }
  );

  const { data: lookupsData } = useQuery(
    ["GetFilterDataCollection-personal"],
    () =>
      FILTER_API.GetFilterDataCollection(
        false,
        [3000, 37000, 13000, 1000, 6000, 5000, 2000, 28000, 7000, 8000, 9000, 4]
      ),
    {
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (reloadPage) getPersonalInformation();
  }, [reloadPage]);

  return (
    <form style={{ width: "100%" }}>
      <Box display="flex" flexDirection="column" width="100%" alignItems="flex-end">
        <Typography variant="h1">اطلاعات فردی</Typography>
        <Grid
          container
          flexDirection="row-reverse"
          sx={{ width: "100%", maxWidth: "800px" }}
          mt={2}
        >
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="قومیت"
              data={lookupsData?.data?.data[2000]}
              value={data.ethnicityCode}
              labelFlex={2}
              flex={3.9}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "ethnicityCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
              idKey="code"
              error={"ethnicityCode" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} sx={Style.itemGrid}>
            <BaseTextArea
              label="توضیحات"
              value={data.ethnicityDescription}
              maxRows={3}
              placeholder=""
              labelFlex={{ xs: 2.1, sm: 1.85, md: 1.85, lg: 1.85 }}
              flex={{ xs: 3.9, sm: 9, md: 9.3, lg: 9.3 }}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "ethnicityDescription",
                    value: event.target.value,
                    required: false,
                  })
                )
              }
              disabled={disabled}
              error={"ethnicityDescription" in pageErrors}
            />
          </Grid>
          {userInfo.genderTypeEnum === 1 && (
            <BaseRadioGroup
              label="وضعیت نظام وظیفه"
              variant="h2"
              labelGridSize={{ xs: 3.8, sm: 1.8 }}
              inputGridSize={{ xs: 8.2, sm: 10.2 }}
              containerGridSize={{
                xs: 12,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                alignContent: "flex-end",
              }}
              RadioGridsize={{ xs: 12, sm: 12, md: 2.4 }}
              data={[
                {
                  name: "دانشجو",
                  code: 5,
                },
                {
                  name: "در حال خدمت",
                  code: 1,
                },
                {
                  name: "معاف",
                  code: 4,
                },
                {
                  name: "غیبت",
                  code: 2,
                },
                {
                  name: "پایان خدمت",
                  code: 3,
                },
              ]}
              value={data.militaryServiceStatus !== undefined ? data.militaryServiceStatus : ""}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "militaryServiceStatus",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
            />
          )}
          {userInfo.genderTypeEnum === 1 && (
            <Grid item xs={12} sm={12} md={12} lg={12} sx={Style.itemGrid}>
              <BaseTextArea
                label="توضیحات"
                value={data.militaryServiceDescription}
                maxRows={3}
                placeholder=""
                labelFlex={{ xs: 2, sm: 1.85, md: 1.85, lg: 1.85 }}
                flex={{ xs: 3.7, sm: 9, md: 9.3, lg: 9.3 }}
                onChange={(event) =>
                  dispatch(
                    updatePersonalInformationData({
                      key: "militaryServiceDescription",
                      value: event.target.value,
                      required: false,
                    })
                  )
                }
                disabled={disabled}
                error={"militaryServiceDescription" in pageErrors}
              />
            </Grid>
          )}
        </Grid>
        <Box mt="1.3rem" mb="2rem" width="100%">
          <Divider />
        </Box>
        <Typography variant="h1">اطلاعات اعتقادی</Typography>
        <Grid
          container
          flexDirection="row-reverse"
          sx={{ width: "100%", maxWidth: "800px" }}
          mt={2}
        >
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="دین"
              labelKey="name"
              idKey="code"
              labelFlex={2}
              flex={3.9}
              value={data.religionCode}
              onChange={(event) => {
                dispatch(
                  updatePersonalInformationData({
                    key: "religionCode",
                    value: event.target.value,
                    required: true,
                  })
                );
                dispatch(
                  updatePersonalInformationData({
                    key: "religionTypeCode",
                    value: "",
                    required: true,
                  })
                );
              }}
              data={lookupsData?.data?.data[3000]}
              disabled={disabled}
              error={"religionCode" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="مذهب"
              value={data.religionTypeCode}
              labelFlex={2}
              flex={3.9}
              onChange={(event) => {
                dispatch(
                  updatePersonalInformationData({
                    key: "religionTypeCode",
                    value: event.target.value,
                    required: true,
                  })
                );
              }}
              data={lookupsData?.data?.data[data.religionCode]}
              disabled={disabled || parseInt(data.religionCode) !== 4}
              idKey="code"
              error={"religionTypeCode" in pageErrors}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="تقید مذهبی"
              data={lookupsData?.data?.data[28000]}
              value={data.commitmentRateCode}
              labelFlex={2}
              flex={3.9}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "commitmentRateCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              idKey="code"
              disabled={disabled}
              error={"commitmentRateCode" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="گرایش سیاسی "
              value={data.politicalOrientationCode}
              labelFlex={2}
              flex={3.9}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "politicalOrientationCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              data={lookupsData?.data?.data[5000]}
              idKey="code"
              disabled={disabled}
              error={"politicalOrientationCode" in pageErrors}
            />
          </Grid>
        </Grid>

        <Box mt="1.3rem" mb="2rem" width="100%">
          <Divider />
        </Box>
        <Typography variant="h1">اطلاعات ظاهری</Typography>

        <Grid
          container
          flexDirection="row-reverse"
          sx={{ width: "100%", maxWidth: "800px" }}
          mt={2}
        >
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Input
              label="قد"
              placeholder="170"
              name="height"
              id="height"
              value={data.height}
              type="number"
              labelFlex={2}
              flex={4}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "height",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
              error={"height" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Input
              label="وزن"
              placeholder="80"
              name="weight"
              id="weight"
              value={data.weight}
              type="number"
              labelFlex={2}
              flex={4}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "weight",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
              error={"weight" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="رنگ پوست"
              value={data.skinColorCode}
              name="skinColorCode"
              labelFlex={2}
              flex={3.9}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "skinColorCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              data={lookupsData?.data?.data[6000]}
              idKey="code"
              disabled={disabled}
              error={"skinColorCode" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="رنگ چشم"
              value={data.eyeColorCode}
              name="eyeColorCode"
              labelFlex={2}
              flex={3.9}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "eyeColorCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              data={lookupsData?.data?.data[1000]}
              idKey="code"
              disabled={disabled}
              error={"eyeColorCode" in pageErrors}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="نوع پوشش"
              value={data.clothingTypeCode}
              name="clothingType"
              labelFlex={2}
              flex={3.9}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "clothingTypeCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              data={lookupsData?.data?.data[userInfo.genderTypeEnum === 1 ? 37000 : 13000]}
              idKey="code"
              disabled={disabled}
              error={"clothingTypeCode" in pageErrors}
            />
          </Grid>

          <Grid item xs={12} sx={Style.itemGrid}>
            <BaseTextArea
              label="توضیحات"
              placeholder="درصورت نیاز توضیح دهید"
              value={data.clothingDescription}
              name="clothingDescription"
              labelFlex={{ xs: 2, sm: 1.85, md: 1.87, lg: 1.87 }}
              flex={{ xs: 3.7, sm: 9, md: 9.3, lg: 9.3 }}
              onChange={(event) =>
                dispatch(
                  updatePersonalInformationData({
                    key: "clothingDescription",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
              error={"clothingDescription" in pageErrors}
            />
          </Grid>
        </Grid>
        <Box mt="1.3rem" mb="2rem" width="100%">
          <Divider />
        </Box>
        <Typography variant="h1">سابقه بیماری</Typography>
        <Grid
          container
          flexDirection="row-reverse"
          sx={{ width: "100%", maxWidth: "800px" }}
          mt="0.75rem"
        >
          <Grid item xs={12} sx={Style.itemGrid}>
            <Grid
              container
              flexDirection="row-reverse"
              alignItems="center"
              sx={{ width: "100%", maxWidth: "800px" }}
            >
              <Grid item xs={12} sm={12} md={4} lg={4} mb={{ xs: "1rem", md: 0 }}>
                <BaseSwitch
                  label="معلولیت دارم"
                  checked={data.isDisabled}
                  onChange={(event) => {
                    dispatch(
                      updatePersonalInformationData({
                        key: "isDisabled",
                        value: event.target.checked,
                        required: true,
                      })
                    );
                    if (!event.target.checked)
                      dispatch(
                        updatePersonalInformationData({
                          key: "disabledDescription",
                          value: "",
                          required: true,
                        })
                      );
                  }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={8} lg={8}>
                <BaseTextArea
                  label="توضیحات"
                  placeholder="نوع معلولیت شما چیست؟"
                  value={data.isDisabled ? data.disabledDescription : ""}
                  name="disabledDescription"
                  labelFlex={{ xs: 2, sm: 2, md: 2, lg: 2 }}
                  flex={{ xs: 3.8, sm: 10, md: 10, lg: 10.2 }}
                  onChange={(event) =>
                    dispatch(
                      updatePersonalInformationData({
                        key: "disabledDescription",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  disabled={!data.isDisabled || disabled}
                  error={"disabledDescription" in pageErrors}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={Style.itemGrid}>
            <Grid
              container
              flexDirection="row-reverse"
              alignItems="center"
              sx={{ width: "100%", maxWidth: "800px" }}
            >
              <Grid item xs={12} sm={12} md={4} lg={4} mb={{ xs: "1rem", md: 0 }}>
                <BaseSwitch
                  label="بیماری خاصی دارم"
                  checked={data.isSpecialPatient}
                  onChange={(event) => {
                    dispatch(
                      updatePersonalInformationData({
                        key: "isSpecialPatient",
                        value: event.target.checked,
                        required: true,
                      })
                    );
                    if (!event.target.checked)
                      dispatch(
                        updatePersonalInformationData({
                          key: "specialPatientDescription",
                          value: "",
                          required: true,
                        })
                      );
                  }}
                  disabled={disabled}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={8} lg={8}>
                <BaseTextArea
                  label="توضیحات"
                  placeholder="نوع بیماری و داروی مصرفی شما چیست؟"
                  value={data.isSpecialPatient ? data.specialPatientDescription : ""}
                  labelFlex={{ xs: 2, sm: 2, md: 2, lg: 2 }}
                  flex={{ xs: 4, sm: 10, md: 10, lg: 10.2 }}
                  name="specialPatientDescription"
                  onChange={(event) =>
                    dispatch(
                      updatePersonalInformationData({
                        key: "specialPatientDescription",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  disabled={!data.isSpecialPatient || disabled}
                  error={"specialPatientDescription" in pageErrors}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Box mt="1.3rem" mb="2rem" width="100%">
          <Divider />
        </Box>
        <Box
          display="flex"
          flexDirection={{
            xs: "column-reverse",
            sm: "column-reverse",
            md: "row",
          }}
          justifyContent="flex-end"
          alignItems={{ xs: "flex-end", sm: "flex-end", md: "flex-start" }}
          width={{ xs: "100%", sm: "100%", md: "100%" }}
          my={2}
        >
          {["7005", "7004", "7003", "7002"].includes(data.marriageHistoryCode) && (
            <Box
              width={{ xs: "90%", sm: "90%", md: "65%" }}
              border="1px solid #DBDAE2"
              borderRadius={2}
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
              p={2}
            >
              <Grid item xs={12}>
                <Typography variant="h1" sx={{ direction: "rtl" }}>
                  اطلاعات تکمیلی تاهل
                </Typography>
              </Grid>
              <Grid
                container
                display="flex"
                flexDirection={{ xs: "column-reverse", sm: "row", md: "row" }}
                justifyContent="flex-end"
                alignItems={{ xs: "flex-end", sm: "flex-start", md: "center" }}
                my={1}
              >
                <Grid item xs={12} sm={10} md={6} lg={6} xl={5}>
                  <DatePicker
                    value={data.separationDate}
                    onChange={(value) =>
                      dispatch(
                        updatePersonalInformationData({
                          key: "separationDate",
                          value: value ? value.format() : "",
                          required: true,
                        })
                      )
                    }
                    disabled={isSingle || disabled}
                    label="تاریخ جدایی/ فوت/ شهادت"
                    labelFlex={{ xs: 4.5, sm: 3.18, md: 5.6, lg: 4.7, xl: 6 }}
                    flex={{ xs: 7, sm: 8, md: 6, lg: 5, xl: 4.45 }}
                    id="separationDate"
                    name="separationDate"
                    error={"separationDate" in pageErrors}
                  />
                </Grid>
              </Grid>
              <Grid
                container
                display="flex"
                flexDirection={{ xs: "row", sm: "row", md: "row" }}
                alignItems={{ xs: "flex-end", sm: "flex-start", md: "center" }}
                justifyContent="flex-end"
                my={1}
              >
                <Grid
                  item
                  xs={12}
                  sm={10}
                  md={8}
                  lg={7}
                  xl={7}
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-end"
                >
                  <Grid
                    item
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    xs={7}
                    sm={8}
                    md={8.5}
                    lg={6.29}
                    xl={7.1}
                  >
                    <Grid item xs={1} sm={1} md={1} lg={1} xl={1} mx={1}>
                      <Typography fontSize="14px" color="#4C466F">
                        ماه
                      </Typography>
                    </Grid>
                    <Grid item xs={5} sm={4} md={2} lg={5} xl={2.08}>
                      <Input
                        pass="number"
                        flex={12}
                        value={data.marriageDurationMonth}
                        onChange={(event) =>
                          dispatch(
                            updatePersonalInformationData({
                              key: "marriageDurationMonth",
                              value: event.target.value,
                              required: true,
                            })
                          )
                        }
                        disabled={isSingle || disabled}
                        error={"marriageDurationYear" in pageErrors}
                      />
                    </Grid>
                    <Grid item xs={1} sm={1} md={1.1} lg={1} xl={1.1} mx={2}>
                      <Typography fontSize="14px" color="#4C466F">
                        سال
                      </Typography>
                    </Grid>
                    <Grid item xs={5} sm={4} md={2} lg={5} xl={2}>
                      <Input
                        pass="number"
                        flex={12}
                        value={data.marriageDurationYear}
                        onChange={(event) =>
                          dispatch(
                            updatePersonalInformationData({
                              key: "marriageDurationYear",
                              value: event.target.value,
                              required: true,
                            })
                          )
                        }
                        disabled={isSingle || disabled}
                        error={"marriageDurationYear" in pageErrors}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    sm={3.4}
                    md={5}
                    lg={4.95}
                    xl={4.95}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    <Typography variant="h2" textAlign="right">
                      مدت زمان زندگی
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid minWidth="100%" py={2}>
                <BaseTextArea
                  value={data.separationDescription}
                  label="توضیحات"
                  onChange={(event) =>
                    dispatch(
                      updatePersonalInformationData({
                        key: "separationDescription",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  disabled={isSingle || disabled}
                  placeholder="توضیحات"
                  flex={{ xs: 3.7, sm: 7, md: 8, lg: 8, xl: 8 }}
                  labelFlex={{ xs: 1.95, sm: 2.2, md: 2.62, xl: 2.55 }}
                  minRows={1}
                  error={"separationDescription" in pageErrors}
                />
              </Grid>
              <Box
                display="flex"
                flexDirection={{ xs: "column-reverse", sm: "row", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-end", sm: "flex-start", md: "flex-start", xl: "center" }}
                width="100%"
                my={1}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  width={{ xs: "100%", sm: "80%", md: "80%" }}
                >
                  {data.children.map((child, index) => (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-end"
                      width="100%"
                      key={index}
                    >
                      <Box
                        display="flex"
                        flexDirection={{
                          xs: "column-reverse",
                          sm: "column-reverse",
                          md: "row",
                        }}
                        alignItems={{ xs: "flex-end", sm: "flex-end", md: "center" }}
                        justifyContent="space-between"
                        border="1px solid #DBDAE2"
                        borderRadius={2}
                        width="97%"
                        mb={1}
                        p={1}
                      >
                        <Grid
                          item
                          display="flex"
                          flexDirection={{
                            xs: "column-reverse",
                            sm: "row",
                            md: "row",
                          }}
                          alignItems={{ xs: "flex-end", sm: "center", md: "center" }}
                          mt={{ xs: 1, sm: 1, md: 0, lg: 0 }}
                          xs={12}
                          sm={12}
                          md={6}
                          lg={6}
                          ml={1}
                          sx={{ width: "100%" }}
                        >
                          <IconButton
                            sx={{
                              backgroundColor: "#EAEAEA",
                              borderRadius: "0.6rem",
                              marginRight: "1rem",
                            }}
                            disabled={disabled}
                            onClick={() => dispatch(removeChild(index))}
                          >
                            <HiOutlineTrash />
                          </IconButton>
                          <DatePicker
                            value={child.birthDate}
                            onChange={(event) =>
                              dispatch(
                                editChild({
                                  index,
                                  fieldName: "birthDate",
                                  value: event ? event.format() : "",
                                })
                              )
                            }
                            labelFlex={4}
                            flex={{ xs: 8, sm: 7, md: 8 }}
                            disabled={isSingle || disabled}
                            label="تاریخ تولد"
                            id={`birthDate_child_${index}`}
                            name={`birthDate_child_${index}`}
                            error={`birthDate_${index + 1}` in pageErrors}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={5} lg={5} ml={1} sx={{ width: "100%" }}>
                          <Select
                            data={gender}
                            value={child.gender}
                            flex={8}
                            labelFlex={4}
                            onChange={(event) =>
                              dispatch(
                                editChild({
                                  index,
                                  fieldName: "gender",
                                  value: event.target.value === "" ? 0 : event.target.value,
                                })
                              )
                            }
                            label="جنسیت"
                            disabled={isSingle || disabled}
                            labelKey="name"
                            idKey="value"
                            name={`gender_${index}`}
                            error={`gender_${index + 1}` in pageErrors}
                          />
                        </Grid>
                      </Box>
                    </Box>
                  ))}
                  {!disabled && (
                    <AddField handleAdd={() => dispatch(addNewChild())} title="اضافه کردن فرزند" />
                  )}
                </Box>
                <Grid item xs={3}>
                  {(!disabled || data.children.length !== 0) && (
                    <Typography variant="h2" mb={{ xs: 1, sm: 0, md: 0 }} mt={2}>
                      اطلاعات فرزندان
                    </Typography>
                  )}
                </Grid>
              </Box>
              <Typography variant="h2" my={2} textAlign="right">
                آیا فرزندان شما با شما زندگی می کنند ؟ وضعیت آنها را توضیح دهید.
              </Typography>
              <Grid minWidth="100%">
                <BaseTextArea
                  value={data.isLivingWithChildrenDescription}
                  onChange={(event) =>
                    dispatch(
                      updatePersonalInformationData({
                        key: "isLivingWithChildrenDescription",
                        value: event.target.value,
                        required: true,
                      })
                    )
                  }
                  disabled={isSingle || disabled}
                  placeholder="توضیحات"
                  flex={12}
                  minRows={3}
                  error={"isLivingWithChildrenDescription" in pageErrors}
                />
              </Grid>
            </Box>
          )}

          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            width={{ xs: "100%", sm: "100%", md: "30%" }}
          >
            <BaseRadioGroup
              label="وضعیت تاهل"
              onChange={handleMaritalStatusChange}
              data={lookupsData?.data?.data[7000]}
              value={data.marriageHistoryCode !== undefined ? data.marriageHistoryCode : ""}
              disabled={disabled}
              inputGridSize={{ xs: 12 }}
              labelGridSize={{
                xs: 12,
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "row" },
                alignItems: { sm: "center" },
              }}
              error={pageErrors["marriageHistoryCode"]}
            />
            {/* <Typography variant="h1">وضعیت تاهل</Typography>
            <RadioGroup
              value={data.marriageHistoryCode !== undefined ? data.marriageHistoryCode : ""}
              onChange={handleMaritalStatusChange}
              disabled={disabled}
            >
              {maritalStatus?.data?.data.data.map((status) => (
                <BaseRadio
                  key={status.code}
                  text={status.name}
                  value={status.code}
                  disabled={disabled}
                />
              ))}
            </RadioGroup> */}
          </Box>
        </Box>
        <Box mt="1.3rem" mb="2rem" width="100%">
          <Divider />
        </Box>
        <BaseRadioGroup
          label="اقامت در خارج از کشور"
          onChange={(event) => {
            dispatch(
              updatePersonalInformationData({
                key: "residenceAbroadTypeCode",
                value: event.target.value,
                required: true,
              })
            );
          }}
          containerGridSize={{
            display: "flex",
            flexDirection: "column-reverse",
            alignItems: "flex-end",
            alignContent: "flex-end",
            width: "100%",
            maxWidth: "800px",
          }}
          inputGridSize={{ xs: 12 }}
          RadioGridsize={{ xs: 12, md: 6 }}
          labelGridSize={{
            xs: 12,
          }}
          data={lookupsData?.data?.data[8000]}
          value={data.residenceAbroadTypeCode !== undefined ? data.residenceAbroadTypeCode : ""}
          disabled={disabled}
          error={pageErrors["residenceAbroadTypeCode"]}
        />
        <Box mt="1.3rem" mb="2rem" width="100%">
          <Divider />
        </Box>
        <BaseRadioGroup
          label="شرایط اولین ملاقات"
          onChange={(event) => {
            dispatch(
              updatePersonalInformationData({
                key: "firstDateTypeCode",
                value: event.target.value,
                required: true,
              })
            );
          }}
          data={lookupsData?.data?.data[9000]}
          value={data.firstDateTypeCode !== undefined ? data.firstDateTypeCode : ""}
          disabled={disabled}
          error={pageErrors["firstDateTypeCode"]}
          containerGridSize={{
            display: "flex",
            flexDirection: "column-reverse",
            alignItems: "flex-end",
            alignContent: "flex-end",
            width: "100%",
            maxWidth: "800px",
          }}
          inputGridSize={{ width: "100%" }}
          RadioGridsize={{ xs: 12, md: 6 }}
          labelGridSize={{
            xs: 12,
            display: { xs: "flex", sm: "block" },
            flexDirection: "column-reverse",
          }}
        />
        <Box mt="1.3rem" mb="2rem" width="100%">
          <Divider />
        </Box>
        <Box display="flex" alignItems="center">
          <Typography variant="h1" ml={3}>
            سایر
          </Typography>
        </Box>
        {/* //! inqsmt felan pak nashe ta taklifesh moshakhas beshe */}
        {/* <BaseCheckbox
          label="سیگار می کشم"
          value="isSmokingCigaret"
          checked={data.isSmokingCigaret}
          onChange={(event) =>
            dispatch(
              updatePersonalInformationData({
                key: "isSmokingCigaret",
                value: event.target.checked,
                required: true,
              })
            )
          }
          disabled={disabled}
        />

        <BaseCheckbox
          label=" قلیان می کشم"
          value="isSmokingHookah"
          checked={data.isSmokingHookah}
          onChange={(event) =>
            dispatch(
              updatePersonalInformationData({
                key: "isSmokingHookah",
                value: event.target.checked,
                required: true,
              })
            )
          }
          disabled={disabled}
        /> */}
        <Box display="flex" alignItems="center" mr={2.5}>
          <BaseCheckbox
            label="از دخانیات استفاده می‌کنم"
            value="drugUsed"
            checked={data.drugUsed}
            onChange={(event) =>
              dispatch(
                updatePersonalInformationData({
                  key: "drugUsed",
                  value: event.target.checked,
                  required: true,
                })
              )
            }
            disabled={disabled}
          />
        </Box>
        <Box mt="1.3rem" mb="2rem" width="100%">
          <Divider />
        </Box>
        <Typography variant="h1" mb={2}>
          توانایی ها
        </Typography>
        <Box
          width="100%"
          borderRadius="16px"
          overflow="hidden"
          border="1px solid #ebedfa"
          boxShadow="2px 2px 8px rgba(0, 0, 0, 0.25)"
          my={1}
        >
          <Accordion
            sx={{ width: "100%", mt: "20px" }}
            expanded={expanded === "panel2"}
            onChange={handleChange("panel2")}
          >
            <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
              <Grid container display="flex" justifyContent="flex-end">
                <Typography variant="h2" textAlign="right">
                  مهارت‌های علمی
                </Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <BaseTextArea
                maxRows={6}
                minRows={6}
                flex={12}
                value={data.scienceCapabilities}
                onChange={(event) =>
                  dispatch(
                    updatePersonalInformationData({
                      key: "scienceCapabilities",
                      value: event.target.value,
                      required: false,
                    })
                  )
                }
                disabled={disabled}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box
          width="100%"
          borderRadius="16px"
          overflow="hidden"
          border="1px solid #ebedfa"
          boxShadow="2px 2px 8px rgba(0, 0, 0, 0.25)"
          my={1}
        >
          <Accordion
            sx={{ width: "100%", mt: "20px" }}
            expanded={expanded === "panel3"}
            onChange={handleChange("panel3")}
          >
            <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
              <Grid container display="flex" justifyContent="flex-end">
                <Typography variant="h2" textAlign="right">
                  مهارت‌های فنی و حرفه‌ای
                </Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <BaseTextArea
                minRows={6}
                maxRows={6}
                value={data.musicCapabilities}
                flex={12}
                onChange={(event) =>
                  dispatch(
                    updatePersonalInformationData({
                      key: "musicCapabilities",
                      value: event.target.value,
                      required: false,
                    })
                  )
                }
                disabled={disabled}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box
          width="100%"
          borderRadius="16px"
          overflow="hidden"
          border="1px solid #ebedfa"
          boxShadow="2px 2px 8px rgba(0, 0, 0, 0.25)"
          my={1}
        >
          <Accordion
            sx={{ width: "100%", mt: "20px" }}
            expanded={expanded === "panel1"}
            onChange={handleChange("panel1")}
          >
            <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
              <Grid container display="flex" justifyContent="flex-end">
                <Typography variant="h2" textAlign="right">
                  مهارت‌های هنری
                </Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <BaseTextArea
                maxRows={6}
                minRows={6}
                flex={12}
                value={data.artCapabilities}
                onChange={(event) =>
                  dispatch(
                    updatePersonalInformationData({
                      key: "artCapabilities",
                      value: event.target.value,
                      required: false,
                    })
                  )
                }
                disabled={disabled}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box
          width="100%"
          borderRadius="16px"
          overflow="hidden"
          border="1px solid #ebedfa"
          boxShadow="2px 2px 8px rgba(0, 0, 0, 0.25)"
          my={1}
        >
          <Accordion
            sx={{ width: "100%", mt: "20px" }}
            expanded={expanded === "panel4"}
            onChange={handleChange("panel4")}
          >
            <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
              <Grid container display="flex" justifyContent="flex-end">
                <Typography variant="h2" textAlign="right">
                  مهارت‌های ورزشی
                </Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <BaseTextArea
                maxRows={6}
                flex={12}
                minRows={6}
                value={data.sportCapabilities}
                onChange={(event) =>
                  dispatch(
                    updatePersonalInformationData({
                      key: "sportCapabilities",
                      value: event.target.value,
                      required: false,
                    })
                  )
                }
                disabled={disabled}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box
          width="100%"
          borderRadius="16px"
          overflow="hidden"
          border="1px solid #ebedfa"
          boxShadow="2px 2px 8px rgba(0, 0, 0, 0.25)"
          my={1}
        >
          <Accordion
            sx={{ width: "100%", mt: "20px" }}
            expanded={expanded === "panel5"}
            onChange={handleChange("panel5")}
          >
            <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
              <Grid container display="flex" justifyContent="flex-end">
                <Typography variant="h2" textAlign="right">
                  علایق، سرگرمی‌ها، تفریحات
                </Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <BaseTextArea
                maxRows={6}
                minRows={6}
                flex={12}
                value={data.otherInterests}
                onChange={(event) =>
                  dispatch(
                    updatePersonalInformationData({
                      key: "otherInterests",
                      value: event.target.value,
                      required: false,
                    })
                  )
                }
              />
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box
          width="100%"
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row", md: "row" },
            justifyContent: "center",
            alignItems: "center",
            pt: 2,
            mt: "2rem",
          }}
        >
          <Button
            type="button"
            onClick={() => handleSubmitForm("/personal-info/education")}
            variant="m1"
            sx={{ mr: 1, width: "165px", my: { xs: 1, sm: 0, md: 0 } }}
          >
            بعدی
          </Button>
          <Button
            type="button"
            variant="m2"
            onClick={() => handleSubmitForm("/personal-info/base-info")}
            sx={{ mr: 1 }}
          >
            قبلی
          </Button>
        </Box>
      </Box>
    </form>
  );
}

const Style = {
  itemGrid: {
    boxSizing: "border-box",
    paddingLeft: { xs: 0, sm: "0.5rem", md: "1rem", lg: "1rem" },
    marginBottom: "0.75rem",
  },
};

export default PersonalInfo;
